import React, { useEffect, useState } from "react";
import { all_routes } from "../../../router/all_routes";
import { useNavigate } from "react-router";
import Cookies from "js-cookie";
import { useUserPermissions } from "../../../UserPermissionsContext";
import { Select, Spin } from "antd";
import { format } from "date-fns";
import Table from "../../../../core/common/dataTable/index";
import { Link } from "react-router-dom";
import { DateRange } from "react-date-range";
import { CloseCircleOutlined } from "@ant-design/icons";

interface StudentData {
  id: number;
  name: string;
  scs_number: string;
  student_class: string;
  building_category: string;
  branch: string;
  sale_date: string; // Format: "YYYY-MM-DD HH:MM:SS"
  program: string;
}

const SedSales = () => {
  const { Option } = Select;
  const routes = all_routes;
  const navigate = useNavigate();
  const jwtToken = Cookies.get("authToken");
  const { userPermissions, webApi } = useUserPermissions();
  const [isLoading, setIsLoading] = useState(false);
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchText, setSearchText] = useState<string>("");
  const [branchDropdown, setBranchDropdown] = useState<any[]>([]);
  const [programDropdown, setProgramDropdown] = useState<any[]>([]);
  const [classDropdown, setClassDropdown] = useState<any[]>([]);
  const [agentsDropdown, setAgentsDropdown] = useState<any[]>([]);
  const [data, setData] = useState<StudentData[]>([]);
  const [showDateRange, setShowDateRange] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectionRange, setSelectionRange] = useState({
    startDate: new Date(),
    endDate: new Date(),
    key: "selection",
  });
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [filters, setFilters] = useState(() => {
    const savedFilters = localStorage.getItem("sedsales");
    return savedFilters
      ? JSON.parse(savedFilters)
      : {
          name: "",
          scs_number: "",
          branch: "",
          building_category: "",
          program: "",
          student_class: "",
        };
  });
  useEffect(() => {
    localStorage.setItem("sedsales", JSON.stringify(filters));
  }, [filters]);
  const [appliedFilters, setAppliedFilters] = useState(filters);

  const checkPermission = (action: any, subject: any) => {
    return userPermissions.some(
      (ability) => ability.action === action && ability.subject === subject,
    );
  };

  const FILTERS = [
    { label: "SCTS", value: "1" },
    { label: "ETS", value: "2" },
  ];

  const options = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${jwtToken}`,
    },
  };

  useEffect(() => {
    if (!checkPermission("view_sedsales", "sedsales")) {
      navigate(routes.crmLandingPage);
      return;
    }
    fetchData();
  }, [
    userPermissions,
    appliedFilters,
    currentPage,
    searchText,
    pageSize,
    toDate,
    fromDate,
  ]);

  // Program Dropdown
  const fetchPrograms = async () => {
    try {
      const res = await fetch(
        `${webApi}/sed/sed_program_dropdown_for_sed_sales/`,
        options,
      );
      const data = await res.json();
      setProgramDropdown(data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchPrograms();
  }, []);

  // Branch Dropdown
  const fetchBranches = async () => {
    try {
      const res = await fetch(
        `${webApi}/sed/branches_dropdown_for_sed_sales/?building_category=${filters.building_category}`,
        options,
      );
      const data = await res.json();
      setBranchDropdown(data);
    } catch (err) {
      console.error(err);
    }
  };

  // Class Dropdown
  const fetchclasses = async () => {
    try {
      const res = await fetch(
        `${webApi}/sed/class_names_dropdown_for_sed_sales/?building_category=${filters.building_category}`,
        options,
      );
      const data = await res.json();
      setClassDropdown(data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchBranches();
    fetchclasses();
  }, [filters.building_category]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const queryParams = new URLSearchParams({
        name: appliedFilters.name || "",
        branch: appliedFilters.branch || "",
        scs_number: appliedFilters.scs_number || "",
        building_category: appliedFilters.building_category || "",
        program: appliedFilters.program || "",
        student_class: appliedFilters.student_class || "",
        page: String(currentPage),
        search: searchText || "",
      });
      queryParams.append("page_size", String(pageSize));
      const response = await fetch(
        `${webApi}/sed/sed_sales/?${queryParams.toString()}&from_date=${fromDate}&to_date=${toDate}`,
        options,
      );
      const result = await response.json();
      if (!response.ok) {
        if (result.code === "token_not_valid") {
          navigate(routes.crmLandingPage);
          return;
        }
        throw new Error(result.detail || "Failed to fetch teachers");
      }
      setData(result.results || []);
      setTotalCount(result.total_count || 0);
    } catch (error) {
      console.error("Teachers fetch error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  //Reusable function to display applied filters
  const renderFilterTag = (
    value: string | null,
    filterKey: keyof typeof appliedFilters,
  ) => {
    if (!value) return null;

    return (
      <span
        style={{
          marginLeft: 8,
          display: "inline-flex",
          alignItems: "center",
        }}
      >
        <span style={{ color: "#1890ff", marginRight: 4 }}>({value})</span>

        <CloseCircleOutlined
          onClick={() => {
            const updatedFilters = {
              ...filters,
              [filterKey]: "",
            };

            setFilters(updatedFilters);
            setAppliedFilters(updatedFilters);
          }}
          style={{ color: "red", cursor: "pointer" }}
        />
      </span>
    );
  };

  const columns = [
    {
      title: (
        <span>
          Branch Category
          {renderFilterTag(
            FILTERS.find(
              (item) =>
                String(item.value) === String(appliedFilters.building_category),
            )?.label || null,
            "building_category",
          )}
        </span>
      ),
      dataIndex: "building_category",
      key: "building_category",
    },

    {
      title: (
        <span>
          Branch
          {renderFilterTag(
            branchDropdown.find(
              (item) =>
                String(item.branch_id) === String(appliedFilters.branch),
            )?.name || null,
            "branch",
          )}
        </span>
      ),
      dataIndex: "branch",
      key: "branch",
    },
    {
      title: (
        <span>
          Name
          {renderFilterTag(appliedFilters.name || null, "name")}
        </span>
      ),

      dataIndex: "name",
      key: "name",
    },
    {
      title: (
        <span>
          SCS Number
          {renderFilterTag(appliedFilters.scs_number || null, "scs_number")}
        </span>
      ),
      dataIndex: "scs_number",
      key: "scs_number",
    },

    {
      title: (
        <span>
          Class
          {renderFilterTag(
            classDropdown.find(
              (item) =>
                String(item.class_name_id) ===
                String(appliedFilters.student_class),
            )?.name || null,
            "student_class",
          )}
        </span>
      ),
      dataIndex: "student_class",
      key: "student_class",
    },
    {
      title: (
        <span>
          Program
          {renderFilterTag(
            programDropdown.find(
              (item) => String(item.id) === String(appliedFilters.program),
            )?.name || null,
            "program",
          )}
        </span>
      ),
      dataIndex: "program",
      key: "program",
    },
    {
      title: "Sale Date",
      dataIndex: "sale_date",
      key: "sale_date",
    },
  ];

  const handleTableChange = (pagination: any) => {
    const { current, pageSize } = pagination;
    setCurrentPage(current);
    setPageSize(pageSize);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
  };

  const handleApplyClick = () => {
    setAppliedFilters(filters);
    setCurrentPage(1);
    setShowFilter(false);
  };
  const handleResetClick = () => {
    const emptyFilters = {
      name: "",
      scs_number: "",
      branch: "",
      building_category: "",
      program: "",
      student_class: "",
    };
    localStorage.removeItem("sedsales");
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setSearchText("");
    setCurrentPage(1);
    setShowFilter(false);
  };

  const handleSelect = (ranges: any) => {
    const { startDate, endDate } = ranges.selection;
    setSelectionRange(ranges.selection);
  };

  const handleApplyDateRange = () => {
    setFromDate(format(selectionRange.startDate, "yyyy-MM-dd"));
    setToDate(format(selectionRange.endDate, "yyyy-MM-dd"));

    setCurrentPage(1);
    setShowDateRange(false);
  };
  const handleResetDateRange = () => {
    setSelectionRange({
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    });

    setFromDate("");
    setToDate("");
    setCurrentPage(1);
    setShowDateRange(false);
  };

  const handleExportClick = () => {
    const queryParams = new URLSearchParams({
      name: appliedFilters.name || "",
      branch: appliedFilters.branch || "",
      scs_number: appliedFilters.scs_number || "",
      building_category: appliedFilters.building_category || "",
      program: appliedFilters.program || "",
      student_class: appliedFilters.student_class || "",
      search: searchText || "",
    });
    let url = `${webApi}/sed/export_sed_sales/?token=${jwtToken}&${queryParams.toString()}&from_date=${fromDate}&to_date=${toDate}`;
    window.location.href = url;
  };

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">SED Sales</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.SEDDashBoard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    SED Sales
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="mb-2">
                {checkPermission("export_sedsales", "sedsales") && (
                  <button
                    className="btn btn-primary me-2"
                    onClick={handleExportClick}
                  >
                    Export
                  </button>
                )}
              </div>
            </div>
          </div>

          <div
            className="card "
            style={{
              width: "100%",
              boxShadow: "0 4px 8px 0 rgba(0,0,0,0.2)",
              borderRadius: "10px",
            }}
          >
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">SED Sales</h4>

              <div className="d-flex align-items-center gap-2 mb-3 position-relative">
                {/* Date Range Button */}
                <button
                  type="button"
                  className="btn btn-outline-primary d-flex align-items-center gap-2"
                  onClick={() => {
                    setShowDateRange(!showDateRange);
                    setShowFilter(false);
                  }}
                >
                  <i className="bi bi-calendar3"></i>
                  {fromDate && toDate
                    ? `${fromDate} - ${toDate}`
                    : "Date Range"}
                </button>

                {/* Calendar Popup */}
                {showDateRange && (
                  <div
                    className="position-absolute bg-white shadow rounded border"
                    style={{
                      top: "45px",
                      right: "110px", // adjust if needed
                      zIndex: 999,
                    }}
                  >
                    <DateRange
                      ranges={[selectionRange]}
                      onChange={handleSelect}
                      moveRangeOnFirstSelection={false}
                      editableDateInputs
                      rangeColors={["#0d6efd"]}
                    />

                    <div className="d-flex justify-content-end gap-2 p-3 border-top">
                      <button
                        className="btn btn-outline-danger"
                        onClick={handleResetDateRange}
                      >
                        Reset
                      </button>

                      <button
                        className="btn btn-primary"
                        onClick={handleApplyDateRange}
                      >
                        Apply
                      </button>

                      <button
                        className="btn btn-secondary"
                        onClick={() => setShowDateRange(false)}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}

                {/* Filter Button */}
                <button
                  className="btn btn-outline-primary"
                  onClick={() => {
                    setShowFilter(!showFilter);
                    setShowDateRange(false);
                  }}
                >
                  <i className="bi bi-funnel me-1"></i>
                  Filters
                </button>
              </div>
            </div>
            {showFilter && (
              <div>
                <div
                  className="card mt-3 shadow-sm"
                  style={{
                    width: "calc(100vw - 20px)",
                    maxWidth: "500px",
                    position: "absolute",
                    right: 0,
                    zIndex: 1000,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <form>
                    <div className="d-flex align-items-center border-bottom p-3">
                      <h4 className="mb-0 me-auto">Filter</h4>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setShowFilter(false)}
                      />
                    </div>

                    <div className="p-3">
                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label"> Name</label>
                            <input
                              type="text"
                              className="form-control"
                              value={filters.name}
                              onChange={(e) =>
                                setFilters({
                                  ...filters,
                                  name: e.target.value,
                                })
                              }
                            />
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label"> SCS Number</label>
                            <input
                              type="text"
                              className="form-control"
                              value={filters.scs_number}
                              onChange={(e) =>
                                setFilters({
                                  ...filters,
                                  scs_number: e.target.value,
                                })
                              }
                            />
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              Building Category
                            </label>
                            <Select
                              showSearch
                              allowClear
                              placeholder="Select Build Category"
                              value={filters.building_category || ""}
                              onChange={(value) =>
                                setFilters({
                                  ...filters,
                                  building_category: value || "",
                                })
                              }
                              style={{ width: "100%" }}
                              optionFilterProp="children"
                              filterOption={(input, option) =>
                                (option?.children as unknown as string)
                                  .toLowerCase()
                                  .includes(input.toLowerCase())
                              }
                            >
                              {FILTERS.map((teamLead) => (
                                <Option
                                  key={teamLead.value}
                                  value={teamLead.value}
                                >
                                  {teamLead.label}
                                </Option>
                              ))}
                            </Select>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Classes</label>
                            <Select
                              showSearch
                              allowClear
                              placeholder="Select Class"
                              value={filters.student_class}
                              onChange={(value) =>
                                setFilters({
                                  ...filters,
                                  student_class: value,
                                })
                              }
                              style={{ width: "100%" }}
                              optionFilterProp="children"
                              filterOption={(input, option) =>
                                (option?.children as unknown as string)
                                  .toLowerCase()
                                  .includes(input.toLowerCase())
                              }
                            >
                              {classDropdown.map((d) => (
                                <Option
                                  key={d.class_name_id}
                                  value={d.class_name_id}
                                >
                                  {d.name}
                                </Option>
                              ))}
                            </Select>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Branch</label>
                            <Select
                              showSearch
                              allowClear
                              placeholder="Select Branch"
                              value={filters.branch}
                              onChange={(value) =>
                                setFilters({
                                  ...filters,
                                  branch: value,
                                })
                              }
                              style={{ width: "100%" }}
                              optionFilterProp="children"
                              filterOption={(input, option) =>
                                (option?.children as unknown as string)
                                  .toLowerCase()
                                  .includes(input.toLowerCase())
                              }
                            >
                              {branchDropdown.map((d) => (
                                <Option key={d.branch_id} value={d.branch_id}>
                                  {d.name}
                                </Option>
                              ))}
                            </Select>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Program</label>
                            <Select
                              showSearch
                              allowClear
                              placeholder="Select Program"
                              value={filters.program || ""}
                              onChange={(value) =>
                                setFilters({
                                  ...filters,
                                  program: value || "",
                                })
                              }
                              style={{ width: "100%" }}
                              optionFilterProp="children"
                              filterOption={(input, option) =>
                                (option?.children as unknown as string)
                                  .toLowerCase()
                                  .includes(input.toLowerCase())
                              }
                            >
                              {programDropdown.map((teamLead) => (
                                <Option key={teamLead.id} value={teamLead.id}>
                                  {teamLead.name}
                                </Option>
                              ))}
                            </Select>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="p-3 border-top d-flex justify-content-end">
                      <button
                        type="button"
                        className="btn btn-light me-3"
                        onClick={handleResetClick}
                      >
                        Reset
                      </button>

                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleApplyClick}
                      >
                        Apply
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
            <div className="card-body p-0 py-3">
              {isLoading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-start",
                    height: "100vh",
                    paddingTop: "20px",
                  }}
                >
                  <Spin /> {/* Display loader while loading */}
                </div>
              ) : (
                <Table
                  columns={columns}
                  dataSource={data}
                  Selection={false}
                  pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    locale: { items_per_page: "" },
                    nextIcon: <span>Next</span>,
                    prevIcon: <span>Prev</span>,
                    defaultPageSize: 10,
                    showSizeChanger: true,
                    total: totalCount,
                    showTotal: (total: any, range: any[]) =>
                      `${range[0]}-${range[1]} of ${total}`,
                    pageSizeOptions: ["10", "25", "100", "500", "1000"],
                  }}
                  onChange={handleTableChange} // Move this outside of pagination
                  onSearch={handleSearch}
                  searchText={searchText}
                />
              )}
            </div>
          </div>
          {/* /Guardians List */}
        </div>
      </div>
    </div>
  );
};

export default SedSales;
